import { fppProvider } from "./class/providers/fppProvider";
import { noOption, noSolutionAvail, perfResultsEnum } from "./dataStruct";
import { kelvin2centigrade, markDownProcessing, meters2feet } from "./functions";

type AeoHogeCondition = { mass: number; oat: number; pa: number };

const CHART_LINE = "[]fppAeoHoge:";

/**
 * The stored form of the AEO HOGE charts: the aircraft model and each
 * location's inputs. The report opens with `renderAeoHogeCharts`, which draws
 * the charts again from these inputs. Inline, each chart carried the chart
 * image as base64 -- about 390 KB, twice per report version.
 */
export function aeoHogeChartLine(model: string, conditions: AeoHogeCondition[]): string {
  return CHART_LINE + model + ":" + conditions.map((c) => [c.mass, c.oat, c.pa].join(",")).join(";");
}

export function parseAeoHogeChartLine(text: string): { model: string; conditions: AeoHogeCondition[] } | undefined {
  const match = /\[\]fppAeoHoge:([^:]*):([-0-9.,;e+]+)/.exec(text);
  if (!match) return undefined;
  const conditions = match[2].split(";").map((c) => c.split(",").map(Number));
  if (conditions.some((c) => c.length !== 3 || c.some((n) => !Number.isFinite(n)))) return undefined;
  return { model: match[1], conditions: conditions.map(([mass, oat, pa]) => ({ mass, oat, pa })) };
}

const feet = (value: number) => (Number.isFinite(value) ? `${Math.round(meters2feet(value))} ft` : "N/A");

// Every condition's lookup is independent -- on the web each is its own
// request, so start them all together instead of one after another. Each
// gets a no-op `.catch` so an early rejection is never reported as
// unhandled while a later lookup is still pending; a rejection still
// surfaces at the `await` in the caller, in condition order.
function lookupCharts(model: string, perfParameters: string, com: any, conditions: AeoHogeCondition[]) {
  const provider = new fppProvider();
  const lookups = conditions.map((input) =>
    provider.getPerfModelDiagram(
      model,
      perfResultsEnum.hoge,
      com,
      ["outsideAirTemp", "grossWeight"],
      [input.oat, input.mass],
      [],
      false,
      perfParameters
    )
  );
  lookups.forEach((lookup) => lookup.catch(() => {}));
  return lookups;
}

/** The AEO chart of one lookup and its ceiling margin in metres. */
function aeoChart(results: any[], input: AeoHogeCondition) {
  const chart = results.find(
    (r) => !r._isOei && !r._violation && Number.isFinite(r.value) && r.value !== noOption && r.value !== noSolutionAvail
  );
  return { chart, margin: chart ? chart.value - input.pa : NaN };
}

/**
 * Draws the AEO HOGE charts for a stored chart line. Processed in condition
 * order, because `createSvgDiagram` numbers its output with a static counter.
 */
export async function renderAeoHogeCharts(
  model: string,
  perfParameters: string,
  com: any,
  conditions: AeoHogeCondition[]
): Promise<string> {
  const lookups = lookupCharts(model, perfParameters, com, conditions);
  const charts: string[] = [];
  for (const [index, input] of conditions.entries()) {
    const { chart, margin } = aeoChart(await lookups[index], input);
    if (!chart?.image) continue;
    const position = chart.lines
      ?.filter((line) => line.points?.length)
      .slice(-1)[0]
      ?.points.slice(-1)[0];
    if (position)
      chart.points = [
        {
          position: [...position],
          desig: `${index === 0 ? "@t/o" : "@ldg"} ${feet(chart.value)}`,
          backgroundColor: margin < 0 ? "#ff0000" : "#00ff00",
          fontColor: "#000000",
        },
      ];
    charts.push(new markDownProcessing().createSvgDiagram(chart));
  }
  return charts.join("\n");
}

/** AEO HOGE returns ceiling in metres, not a maximum mass. */
export async function evaluateAeoHoge(acft: any, com: any, conditions: AeoHogeCondition[]) {
  const margins: number[] = [];
  const rows = [
    "# HOGE — All Engines Operating (AEO)",
    "Takeoff power, heating off. Ceiling margin controls the HOGE performance status.",
    "",
    "| LOC | MASS | OAT | PA | HOGE CEILING | CEILING MARGIN |",
    "|-----|------|-----|----|--------------|----------------|",
  ];
  let hasChart = false;

  const lookups = lookupCharts(acft.model, acft.perfParameters, com, conditions);
  for (const [index, input] of conditions.entries()) {
    const { chart, margin } = aeoChart(await lookups[index], input);
    margins.push(margin);
    rows.push(
      `|${index === 0 ? "{originIcao}" : "{destIcao}"}|${Math.round(input.mass)} kg|${Math.round(kelvin2centigrade(input.oat))} °C|${feet(input.pa)}|${feet(chart?.value)}|${feet(margin)}|`
    );
    hasChart ||= !!chart?.image;
  }
  const chartLine = hasChart ? [aeoHogeChartLine(acft.model, conditions)] : [];
  return { margins, markdown: [...rows, "", ...chartLine, ""].join("\n") };
}
